'use client';

import { useState } from 'react';
import InfiniteGrid from '@/src/components/InfiniteGrid';
import { ProductCard } from '@/src/app/[locale]/stores/[store]/[storeId]/product/components/ProductCard';
import { CMSSectionsAPi } from '../services/getCMS.service';
import { useSearchParams } from 'next/navigation';
import useNavigation from '@/src/lib/hooks/useNavigation';
import Container from '@/src/components/ui/container';
import Typography from '@/src/components/ui/typography';
import { useTranslations } from 'next-intl';
import { ArrowUpDown, CarFront, CirclePercent, SortAscIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/src/components/ui/popover';
import { cn } from '@/src/lib/utils/command';

// ---------- Helper: shared "pill" button styles ----------
const getPillStyles = (isActive: boolean, isGift = false) => {
  if (isActive) {
    return 'bg-black text-white outline outline-4 outline-white -mx-1 z-10';
  }
  if (isGift) {
    return 'bg-linear-gift text-white';
  }
  return 'bg-gray-100 text-gray-700 hover:bg-gray-200';
};

const pillBaseClasses =
  'relative flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300';

// ---------- Sort By Popover ----------

const SORT_OPTIONS = [
  {
    direction: 'Asc' as const,
    translationKey: 'lowToHigh',
    iconClassName: '',
  },
  {
    direction: 'Desc' as const,
    translationKey: 'highToLow',
    iconClassName: 'rotate-180',
  },
] as const;

export const SortByPopover = () => {
  const [open, setOpen] = useState(false);

  const searchParams = useSearchParams();
  const t = useTranslations('product.Filters');

  const { createQueryString, removeQueryString } = useNavigation({
    justUpdateUrl: true,
  });

  const currentSortBy = searchParams.get('sortBy');
  const currentDirection = searchParams.get('direction');

  const isActive = currentSortBy === 'price';

  const selectedOption = SORT_OPTIONS.find((option) => option.direction === currentDirection);

  const triggerLabel = isActive && selectedOption ? t(selectedOption.translationKey) : t('sort');

  const handleSort = (direction: 'Asc' | 'Desc') => {
    const isSameSelection = currentSortBy === 'price' && currentDirection === direction;

    if (isSameSelection) {
      removeQueryString(['sortBy', 'direction']);
    } else {
      createQueryString({
        sortBy: 'price',
        direction,
      });
    }

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`${pillBaseClasses} ${getPillStyles(isActive)}`}>
          <ArrowUpDown className={cn('size-4 text-gray-700', isActive && 'text-white')} />

          <span>{triggerLabel}</span>
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-56">
        <div className="flex flex-col gap-2">
          {SORT_OPTIONS.map(({ direction, translationKey, iconClassName }) => {
            const isSelected = isActive && currentDirection === direction;

            return (
              <button
                key={direction}
                onClick={() => handleSort(direction)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  isSelected ? 'bg-black text-white' : 'hover:bg-gray-100',
                )}
              >
                <SortAscIcon className={cn('size-4 text-inherit', iconClassName)} />

                <span>{t(translationKey)}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ---------- Filters Row ----------
const RenderFilter = () => {
  const searchParams = useSearchParams();
  const t = useTranslations('product.Filters');

  const { createQueryString, removeQueryString } = useNavigation({
    justUpdateUrl: true,
  });

  const toggleFilter = (key: string, value: string) => {
    const currentFilter = searchParams.get(key);

    if (currentFilter === value) {
      removeQueryString(key);
    } else {
      createQueryString({ [key]: value });
    }
  };

  const filtersConfig = [
    {
      key: 'HaveOffers',
      value: 'true',
      label: t('offers'),
      icon: <CirclePercent className="size-5" />,
      iconColor: 'text-red-500',
    },
    {
      key: 'OrderType',
      value: '3',
      label: t('gifts'),
      icon: '🎁',
      isGift: true,
    },
    {
      key: 'IsFastDelivery',
      value: 'true',
      label: t('freeDelivery'),
      icon: <CarFront />,
      iconColor: 'text-green-600',
    },
  ];

  return (
    <>
      <Typography
        variant={'h4'}
        component={'h2'}
        align={'start'}
        className="mb-4 ps-1"
        weight={'semibold'}
      >
        {t('allProducts')}
      </Typography>

      <div className="no-scrollbar w-full overflow-x-auto bg-white py-4">
        <div className="flex min-w-max flex-nowrap items-center gap-3 ps-1">
          <SortByPopover />

          {filtersConfig.map((filter) => {
            const isActive = searchParams.get(filter.key) === filter.value;

            return (
              <button
                key={filter.key}
                onClick={() => toggleFilter(filter.key, filter.value)}
                className={`${pillBaseClasses} ${getPillStyles(isActive, filter.isGift)}`}
              >
                <span className={filter.iconColor}>{filter.icon}</span>
                <span>{filter.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ---------- Main Export ----------
export const AllProducts = ({ pageId }: { pageId: string }) => {
  const searchParams = useSearchParams();

  const filters = {
    orderType: searchParams.get('OrderType') || undefined,
    priceLowToHigh: searchParams.get('PriceLowToHigh') || undefined,
    haveOffers: searchParams.get('HaveOffers') || undefined,
    inStock: searchParams.get('InStock') || undefined,
    isFastDelivery: searchParams.get('IsFastDelivery') || undefined,
    isNearBy: searchParams.get('IsNearBy') || undefined,
    sortBy: searchParams.get('sortBy') || undefined,
    direction: searchParams.get('direction') || undefined,
  };

  const fetchProducts = async (pageParam: number) => {
    return CMSSectionsAPi.getAllProductsFromCMS({
      pageId,
      Page: pageParam.toString(),
      OrderType: filters.orderType,
      PriceLowToHigh: filters.priceLowToHigh,
      HaveOffers: filters.haveOffers,
      InStock: filters.inStock,
      IsFastDelivery: filters.isFastDelivery,
      IsNearBy: filters.isNearBy,
      SortBy: filters.sortBy,
      Direction: filters.direction,
    });
  };

  return (
    <Container>
      <RenderFilter />
      <InfiniteGrid
        queryKey={['all-products', pageId, filters]}
        initialData={undefined}
        fetchFn={fetchProducts}
        pageSize={10}
        gridClassName="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        emptyComponent={
          <div className="py-12 text-center text-gray-500">
            <h2 className="text-xl font-semibold">No products found.</h2>
          </div>
        }
        renderItem={(product) => (
          <ProductCard key={product.integrationId} productDetails={product} />
        )}
      />
    </Container>
  );
};
